import { RefObject, useEffect, useRef } from "react";
import { RaceProgram, RaceStatusEnum } from "@tvg/ts-types/Race";
import { AllOrNone } from "@tvg/ts-types/Generic";

interface MobileScrollProps {
  isMobile: boolean;
  scrollableWrapperRef: RefObject<HTMLUListElement>;
}
interface BaseProps {
  races: Array<RaceProgram>;
  selectedRaceNumber: string | null;
  isOverFlowing: boolean;
}

type Props = BaseProps & AllOrNone<MobileScrollProps>;
// Scroll selected element into view, based on available races to its right or/and left
const useScrollRaceIntoView = ({
  races,
  selectedRaceNumber,
  isOverFlowing,
  scrollableWrapperRef,
  isMobile
}: Props) => {
  const refs = useRef<{ [key: string]: HTMLLIElement }>({});

  useEffect(() => {
    if (races.length) {
      const selectedRaceIndex = races.findIndex(
        (race) => race.raceNumber === selectedRaceNumber
      );

      const availableLeftRaces = races
        .slice(0, selectedRaceIndex)
        .filter(
          (race) =>
            race.status &&
            race.status.code !== RaceStatusEnum.RACE_OFFICIAL &&
            race.status.code !== RaceStatusEnum.RACE_OFF &&
            race.status.code !== RaceStatusEnum.OPEN
        );

      const availableRightRaces = races
        .slice(selectedRaceIndex + 1)
        .filter(
          (race) =>
            race.status && race.status.code !== RaceStatusEnum.RACE_OFFICIAL
        );

      const handleRacePosition = () => {
        if (
          availableLeftRaces.length >= 1 ||
          (availableLeftRaces.length >= 1 && availableRightRaces.length >= 1)
        ) {
          return "end";
        }
        return "start";
      };

      // eslint-disable-next-line security/detect-object-injection
      if (selectedRaceNumber && refs.current[selectedRaceNumber]) {
        // eslint-disable-next-line security/detect-object-injection
        const selectedRace = refs.current[selectedRaceNumber];

        if (isMobile && scrollableWrapperRef?.current) {
          const { width } = selectedRace.getBoundingClientRect();
          const amountToScroll = (+selectedRaceNumber - 2) * width;

          scrollableWrapperRef.current.scrollTo({
            left: amountToScroll,
            behavior: "smooth"
          });
        } else {
          setTimeout(() => {
            selectedRace.scrollIntoView({
              behavior: "smooth",
              block: "nearest",
              inline: handleRacePosition()
            });
          });
        }
      }
    }
  }, [selectedRaceNumber, isOverFlowing]); // dont add races as a dependency

  return [refs] as const;
};

export default useScrollRaceIntoView;
